import {useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {bffApi} from "../../chat/bffApi";
import {FunksjonalitetTilgangId} from "../../../types";
import {GenerateModal} from "./GenerateModal";
import {StandaloneDocumentModal} from "./StandaloneDocumentModal";
import {StandaloneDflowModal} from "./StandaloneDflowModal";
import {ModalType} from "./types";
import type {GenerateButtonsInnerProps} from "./types";
import classes from "./GenerateButtons.module.css";

const BITFLAGS = {
    CHECKLIST: 1,
    HAS_DFLOW: 2,
    STANDALONE: 4,
} as const;

type ModalState = {open: boolean; type: ModalType | null};

const DEFAULT_MODAL_STATE: ModalState = {open: false, type: null};

export const GenerateButtons = ({
    isChecklist = false,
    isStandalone,
    isFetching,
    className,
    topicMeta,
    documents,
    collectionMeta,
    labels,
    status,
}: GenerateButtonsInnerProps) => {
    const {t} = useTranslation();
    const [modalState, setModalState] =
        useState<ModalState>(DEFAULT_MODAL_STATE);
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const hasDflow = !!user?.tilganger?.some((t) =>
        t.produkter?.some((p) => p.id === FunksjonalitetTilgangId.Dflow)
    );

    const currentAccess =
        (isChecklist ? BITFLAGS.CHECKLIST : 0) |
        (hasDflow ? BITFLAGS.HAS_DFLOW : 0) |
        (isStandalone ? BITFLAGS.STANDALONE : 0);

    const handleOpenWord = () => {
        let type: ModalType;
        if (currentAccess & BITFLAGS.STANDALONE) {
            type = ModalType.generateDocument;
        } else if (currentAccess & BITFLAGS.CHECKLIST) {
            type = ModalType.generateChecklist;
        } else {
            type = ModalType.generateDocuments;
        }
        setModalState({open: true, type});
    };

    const handleOpenDflow = () => {
        const type =
            currentAccess & BITFLAGS.STANDALONE
                ? ModalType.sendToDokumentflytStandalone
                : ModalType.sendToDokumentflyt;
        setModalState({open: true, type});
    };

    const handleClose = () => setModalState(DEFAULT_MODAL_STATE);

    const showDflowButton = hasDflow && !isChecklist;

    return (
        <>
            <div
                className={[className, classes.wrapper]
                    .filter(Boolean)
                    .join(" ")}
            >
                {!isFetching && showDflowButton && (
                    <button
                        type="button"
                        className={classes.button}
                        onClick={handleOpenDflow}
                        data-pw="send-dflow-btn"
                    >
                        <Trans
                            i18nKey="Action.SendToDflow"
                            components={{"small-1": <small />}}
                        />
                    </button>
                )}
                {!isFetching && (
                    <button
                        type="button"
                        className={classes.button}
                        onClick={handleOpenWord}
                        data-pw="generate-word-btn"
                    >
                        {currentAccess & BITFLAGS.CHECKLIST ? (
                            t("Action.Generate")
                        ) : (
                            <Trans
                                i18nKey="Action.GenerateToWord"
                                components={{"small-1": <small />}}
                            />
                        )}
                    </button>
                )}
            </div>
            {modalState.type === ModalType.generateDocument && (
                <StandaloneDocumentModal
                    opened={modalState.open}
                    onClose={handleClose}
                    topicMeta={topicMeta}
                    collectionMeta={collectionMeta}
                    documents={documents}
                />
            )}
            {modalState.type === ModalType.sendToDokumentflytStandalone && (
                <StandaloneDflowModal
                    opened={modalState.open}
                    onClose={handleClose}
                    topicMeta={topicMeta}
                    collectionMeta={collectionMeta}
                    documents={documents}
                />
            )}
            {modalState.type !== null &&
                modalState.type !== ModalType.generateDocument &&
                modalState.type !== ModalType.sendToDokumentflytStandalone && (
                    <GenerateModal
                        opened={modalState.open}
                        onClose={handleClose}
                        type={modalState.type}
                        topicMeta={topicMeta}
                        collectionMeta={collectionMeta}
                        documents={documents}
                        labels={labels}
                        status={status}
                    />
                )}
        </>
    );
};
